import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { ArrowRight, Play, ShoppingBag, Video, Check, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const services = [
  {
    icon: ShoppingBag,
    title: "TikTok Shop Setup",
    description: "Get your TikTok Shop up and running, fully connected to your existing e-commerce store. We handle all the technical setup so you can focus on selling.",
    features: [
      "Complete TikTok Shop account setup",
      "Connect to your Shopify, WooCommerce or other platform",
      "Product catalogue sync and optimisation",
      "Payment and shipping configuration",
      "Shop policies and compliance setup",
      "Training on managing your TikTok Shop"
    ]
  },
  {
    icon: Video,
    title: "Videos That Sell",
    description: "Content that stops the scroll and drives sales. We create engaging TikTok videos designed to showcase your products and convert viewers into customers.",
    features: [
      "Product showcase videos that convert",
      "Trending format adaptation for your brand",
      "Hook-driven content strategy",
      "UGC-style authentic content",
      "Batch video production packages",
      "Performance tracking and optimisation"
    ]
  }
];

export default function TikTokServices() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    companyName: "",
    telephone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        telephone: formData.telephone,
        message: `[TikTok Services Enquiry]\nCompany: ${formData.companyName}\n\n${formData.message}`
      });
      setIsSubmitted(true);
      toast.success("Message sent! We'll be in touch soon.");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="tiktok-services-page" className="pt-24 md:pt-32 bg-black min-h-screen">
      <Helmet>
        <title>TikTok Marketing Services | Viral Content & Growth | Barton</title>
        <meta name="description" content="Dominate TikTok with our expert marketing services. We create viral content, manage your account, and grow your following to reach millions of potential customers." />
        <meta property="og:title" content="TikTok Marketing Services | Barton" />
        <meta property="og:description" content="Dominate TikTok with viral content and expert account management. Reach millions of potential customers." />
        <meta property="og:url" content="https://barton.uk/tiktok-services" />
        <meta property="og:image" content="https://barton.uk/og-image.png" />
        <meta name="twitter:title" content="TikTok Marketing Services | Barton" />
        <meta name="twitter:description" content="Dominate TikTok with viral content and expert account management." />
        <meta name="twitter:image" content="https://barton.uk/og-image.png" />
      </Helmet>
      {/* Hero Section */}
      <section className="container-custom py-12 md:py-20">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500/10 text-pink-400 text-sm font-medium mb-6">
            TikTok Shop Services
          </span>
          
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-white">
            Turn TikTok Into Your{" "}
            <span className="text-gradient">Sales Machine</span>
          </h1>
          
          <p className="text-white/60 text-lg md:text-xl leading-relaxed mb-8 max-w-2xl mx-auto">
            Ready to tap into TikTok's 1 billion+ users? We help local businesses set up TikTok Shop and create viral content that drives real sales.
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <Button
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:bg-gradient-to-r from-purple-500 to-pink-500/90 text-white rounded-full px-8 py-6 text-lg"
              onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Get Started <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-black">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4 text-white">
              Our TikTok Services
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              Everything you need to launch and grow on TikTok Shop
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {services.map((service, index) => (
              <div
                key={index}
                data-testid={`tiktok-service-card-${index}`}
                className="bg-[#111111] border border-white/10 rounded-2xl p-8 hover:border-pink-500/50 transition-all duration-300 hover:shadow-[0_0_30px_rgba(248,24,148,0.1)]"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500/10 flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-pink-400" />
                </div>

                <h3 className="font-heading text-2xl font-bold mb-3 text-white">
                  {service.title}
                </h3>

                <p className="text-white/60 mb-6 leading-relaxed">
                  {service.description}
                </p>

                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-pink-400" />
                      </div>
                      <span className="text-white/70 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-16 md:py-24 bg-[#0A0A0A]">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4 text-white">
                Ready to Grow on TikTok?
              </h2>
              <p className="text-white/60 text-lg">
                Fill out the form below and we'll get back to you within 24 hours to discuss how we can help your business succeed on TikTok.
              </p>
            </div>

            {!isSubmitted ? (
              <form onSubmit={handleSubmit} className="bg-[#111111] border border-white/10 rounded-2xl p-8">
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-white">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        className="bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-pink-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-white">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="you@company.com"
                        className="bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-pink-500"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="companyName" className="text-white">Company Name *</Label>
                      <Input
                        id="companyName"
                        name="companyName"
                        type="text"
                        required
                        value={formData.companyName}
                        onChange={handleChange}
                        placeholder="Your business name"
                        className="bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-pink-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="telephone" className="text-white">Phone *</Label>
                      <Input
                        id="telephone"
                        name="telephone"
                        type="tel"
                        required
                        value={formData.telephone}
                        onChange={handleChange}
                        placeholder="Your phone number"
                        className="bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-pink-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-white">Tell us about your business *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="What products do you sell? Do you have an existing online shop? What are your goals for TikTok?"
                      rows={5}
                      className="bg-black/50 border-white/10 text-white placeholder:text-white/30 focus:border-pink-500 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="tiktok-form-submit"
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:bg-gradient-to-r from-purple-500 to-pink-500/90 text-white rounded-full py-6 text-lg"
                  >
                    {isSubmitting ? "Sending..." : "Get Started With TikTok"}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </form>
            ) : (
              <div className="bg-[#111111] border border-pink-500/50 rounded-2xl p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-500 to-pink-500/10 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-pink-400" />
                </div>
                <h3 className="font-heading text-2xl font-bold mb-2 text-white">Message Sent!</h3>
                <p className="text-white/60 mb-6">
                  Thanks for your interest in our TikTok services. We'll be in touch within 24 hours.
                </p>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full border-white/20 text-white hover:bg-white/10 bg-transparent"
                >
                  <Link to="/">Back to Home</Link>
                </Button>
              </div>
            )}

            {/* Contact Info */}
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-6 text-center">
              <a href="tel:01704777881" className="flex items-center justify-center gap-2 text-white/60 hover:text-pink-400 transition-colors">
                <Phone className="w-4 h-4" />
                01704 777881
              </a>
              <a href="mailto:hello@barton.uk" className="flex items-center justify-center gap-2 text-white/60 hover:text-pink-400 transition-colors">
                <Mail className="w-4 h-4" />
                hello@barton.uk
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why TikTok Section */}
      <section className="bg-black py-16 md:py-24">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6 text-white">
                Why Your Business Needs TikTok Shop
              </h2>
              <div className="space-y-4">
                {[
                  {
                    title: "Reach New Customers",
                    description: "TikTok's algorithm shows your products to people actively looking to buy, not just your followers."
                  },
                  {
                    title: "Lower Advertising Costs",
                    description: "Organic reach on TikTok is unmatched. One viral video can generate thousands in sales without ad spend."
                  },
                  {
                    title: "Seamless Shopping Experience",
                    description: "Customers can discover, browse, and buy without ever leaving TikTok. Less friction means more sales."
                  },
                  {
                    title: "Beat Your Competition",
                    description: "Most local businesses aren't on TikTok Shop yet. Get in early and dominate your market."
                  }
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 rounded-xl hover:bg-white/5 transition-colors">
                    <div className={`w-6 h-6 rounded-full ${index % 2 === 0 ? "bg-gradient-to-r from-purple-500 to-pink-500" : "bg-gradient-to-r from-blue-500 to-purple-500"} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1 text-white">{item.title}</h4>
                      <p className="text-white/60 text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl p-1">
                <img
                  src="https://images.unsplash.com/photo-1764162051353-0d3aef372c56?w=800&q=80"
                  alt="Smartphone recording video content"
                  className="w-full h-[400px] object-cover rounded-2xl"
                />
              </div>
              
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-[#111111] border border-white/10 rounded-xl shadow-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                    <Play className="w-5 h-5 text-white fill-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-white">Viral Potential</p>
                    <p className="text-white/50 text-xs">Reach millions organically</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
